/**
 * Shared helpers for rendering CRM communications / inbound email bodies.
 * Keeps plaintext and HTML pipelines consistent across timeline snippets and inbox detail.
 */

import {
  cleanMessageBody,
  normalizeEntityEncodedEmailHtml,
  stripLeadingDetachedCssBlob,
  isHtmlContent,
  stripProofpointEssentialsScannerFooter,
  trimLeadingPlaintextBeforeHtml,
  truncateTrailingAlternateMimeParts,
  stripEmailSanitizerLeakSources,
  removeDangerousParsedHtmlNodes,
  extractTimelineSnippet,
  buildPlaintextEmailDisplayHtml,
} from "./body-utils";

export * from "./body-utils";

export interface PrepareEmailBodyMarkupOptions {
  variant: "full" | "snippet";
  /** Used when `variant` is `snippet` (timeline preview). */
  snippetMaxChars?: number;
}

export interface PrepareEmailBodyMarkupResult {
  /** String to pass to `DOMPurify.sanitize` with `EMAIL_BODY_DOM_PURIFY_CONFIG`. */
  rawMarkupForPurify: string;
  /** True when the body was classified as HTML mail rather than built from escaped plaintext. */
  treatAsHtmlEmail: boolean;
}

/**
 * ## Pipeline: CRM-stored bodies → DOMPurify input
 *
 * Hub timelines and message detail render bodies from `communications.body`, ticket updates,
 * and sync pipelines where **`Content-Type` is not available**. This helper applies a single,
 * documented normalization/classification path before XSS sanitization (caller runs DOMPurify).
 *
 * ### Stages
 *
 * 1. **Whitespace** — {@link cleanMessageBody} (CR/LF normalization, collapse excessive blank lines).
 * 2. **Entity-encoded HTML** — Some stores concatenate plaintext with an HTML section where
 *    markup was entity-escaped (`&lt;table&gt;…`). {@link normalizeEntityEncodedEmailHtml}.
 * 3. **HTML vs plaintext** — {@link isHtmlContent} (structural / entity heuristics when MIME
 *    headers are absent).
 * 3b. **Gateway footers** — {@link stripProofpointEssentialsScannerFooter} removes trailing Proofpoint Essentials
 *    scanner disclaimers (after HTML classification so HTML fragments stay well-formed).
 * 4. **Multipart/alternative-style dedup** — Long plaintext preamble before real HTML is dropped
 *    so the UI does not duplicate plain + HTML. {@link trimLeadingPlaintextBeforeHtml}.
 * 5. **Detached CSS preamble** — Newsletter CSS sometimes appears with no `<style>` wrapper;
 *    {@link stripLeadingDetachedCssBlob} cuts before visible prose/HTML.
 * 6. **Trailing multipart / duplicate HTML** — Some rows append a second MIME part or full HTML
 *    document after the first `</html>`; {@link truncateTrailingAlternateMimeParts} removes that tail.
 * 7. **Leak-prone subtrees** — Regex strip ({@link stripEmailSanitizerLeakSources}) plus
 *    {@link removeDangerousParsedHtmlNodes} (WHATWG `DOMParser`) for tag-based assets.
 * 8. **Plaintext branch** — {@link buildPlaintextEmailDisplayHtml} (escape, linkify `http(s)` URLs).
 *    Snippet mode applies {@link extractTimelineSnippet} first.
 *
 * ### Security
 *
 * Output is **not** safe HTML until sanitized — use DOMPurify per
 * [OWASP DOM XSS prevention](https://cheatsheetseries.owasp.org/cheatsheets/DOM_based_XSS_Prevention_Cheat_Sheet.html).
 * Install {@link registerDomPurifyEmailBodyHooks} once so `<style>` text cannot leak past sanitize.
 */
export function prepareEmailBodyMarkupForDomPurify(
  body: string,
  options: PrepareEmailBodyMarkupOptions
): PrepareEmailBodyMarkupResult {
  const cleaned = cleanMessageBody(body);
  if (!cleaned) {
    return { rawMarkupForPurify: "", treatAsHtmlEmail: false };
  }

  const normalizedForHtml = normalizeEntityEncodedEmailHtml(cleaned);
  const withoutDetachedCss = stripLeadingDetachedCssBlob(normalizedForHtml);
  const htmlMail = isHtmlContent(withoutDetachedCss);
  const withoutProofpoint = stripProofpointEssentialsScannerFooter(withoutDetachedCss, htmlMail);

  let rawMarkupForPurify: string;

  if (htmlMail) {
    const trimmed = trimLeadingPlaintextBeforeHtml(withoutProofpoint);
    const withoutTrailingDup = truncateTrailingAlternateMimeParts(trimmed);
    const regexStripped = stripEmailSanitizerLeakSources(withoutTrailingDup);
    rawMarkupForPurify = removeDangerousParsedHtmlNodes(regexStripped);
  } else {
    const slice =
      options.variant === "snippet"
        ? extractTimelineSnippet(withoutProofpoint, options.snippetMaxChars ?? 300)
        : withoutProofpoint;
    rawMarkupForPurify = buildPlaintextEmailDisplayHtml(slice);
  }

  return {
    rawMarkupForPurify,
    treatAsHtmlEmail: htmlMail,
  };
}

/** Normalize bodies at ingest (timeline fetch) so search and display share one decoding path. */
export function normalizeTimelineStoredBody(raw: string | null | undefined): string {
  const cleaned = cleanMessageBody(raw ?? "");
  return normalizeEntityEncodedEmailHtml(cleaned);
}
